import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  Briefcase, MapPin, Clock, Users, IndianRupee, CalendarDays, ArrowLeft,
  CheckCircle2, Loader2, Upload, FileText, Mail, Share2, Check, X, AlertCircle,
} from 'lucide-react';
import API_BASE from '@/lib/API_BASE';
import { JobCard, JOB_TYPES, WORK_MODES } from './Careers';

const MAX_RESUME_MB = 5;

const fmtDate = (s) => {
  if (!s) return '';
  const [y, m, d] = String(s).split('-');
  if (!y || !m || !d) return s;
  return new Date(Date.UTC(+y, +m - 1, +d))
    .toLocaleDateString('en-IN', { timeZone: 'UTC', day: 'numeric', month: 'long', year: 'numeric' });
};

const EMPTY_FORM = {
  full_name: '', email: '', phone: '', current_location: '', experience_years: '',
  current_company: '', notice_period: '', expected_ctc: '', linkedin_url: '',
  portfolio_url: '', cover_letter: '', website: '',   // `website` is the honeypot
};

const inputCls =
  'w-full h-11 rounded-lg border bg-background px-3 text-sm focus:outline-none ' +
  'focus:ring-2 focus:ring-primary/40 focus:border-primary transition';
const areaCls =
  'w-full rounded-lg border bg-background px-3 py-2.5 text-sm focus:outline-none ' +
  'focus:ring-2 focus:ring-primary/40 focus:border-primary transition';

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-sm font-medium mb-1.5 block">
        {label}{required && <span className="text-primary"> *</span>}
      </span>
      {children}
    </label>
  );
}

function BulletList({ title, items }) {
  if (!items?.length) return null;
  return (
    <section className="mb-8">
      <h2 className="font-heading text-xl font-bold mb-3">{title}</h2>
      <ul className="space-y-2.5">
        {items.map((item, i) => (
          <li key={i} className="flex gap-3 text-muted-foreground leading-relaxed">
            <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-1" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

export default function JobDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [job, setJob]         = useState(null);
  const [related, setRelated] = useState([]);
  const [page, setPage]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [copied, setCopied]   = useState(false);

  const [form, setForm]       = useState(EMPTY_FORM);
  const [resume, setResume]   = useState(null);
  const [errors, setErrors]   = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted]   = useState(false);
  const fileRef  = useRef(null);
  const applyRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    setSubmitted(false);
    setForm(EMPTY_FORM);
    setResume(null);
    window.scrollTo(0, 0);

    axios.get(`${API_BASE}/careers.php`, { params: { action: 'detail', slug } })
      .then((res) => {
        if (cancelled) return;
        if (res.data?.success) {
          setJob(res.data.data);
          setRelated(res.data.related || []);
          setPage(res.data.page || null);
        } else {
          setNotFound(true);
        }
      })
      .catch(() => { if (!cancelled) setNotFound(true); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [slug]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const pickResume = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    const ok = /\.(pdf|doc|docx)$/i.test(file.name);
    if (!ok) { setErrors(['Please upload your resume as a PDF, DOC or DOCX file.']); return; }
    if (file.size > MAX_RESUME_MB * 1024 * 1024) {
      setErrors([`Your resume must be under ${MAX_RESUME_MB} MB.`]); return;
    }
    setErrors([]);
    setResume(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    setErrors([]);

    const local = [];
    if (!form.full_name.trim()) local.push('Your full name is required.');
    if (!form.email.trim()) local.push('Email is required.');
    if (!form.phone.trim()) local.push('Phone number is required.');
    if (!resume) local.push('Please attach your resume.');
    if (local.length) {
      setErrors(local);
      applyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    setSubmitting(true);
    const fd = new FormData();
    fd.append('job_id', job.id);
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    if (resume) fd.append('resume', resume);

    try {
      const res = await axios.post(`${API_BASE}/careers.php?action=apply`, fd);
      if (res.data?.success) {
        setSubmitted(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        setErrors(res.data?.errors || [res.data?.message || 'Something went wrong. Please try again.']);
      }
    } catch (err) {
      // 422 carries the field-level messages the server produced.
      const data = err?.response?.data;
      setErrors(data?.errors || [data?.message || 'Something went wrong. Please try again.']);
      applyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } finally {
      setSubmitting(false);
    }
  };

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) await navigator.share({ title: job.title, url });
      else {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch { /* the user dismissed the share sheet */ }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading role…
      </div>
    );
  }

  if (notFound || !job) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="w-14 h-14 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
            <Briefcase className="w-6 h-6 text-muted-foreground" />
          </div>
          <h1 className="font-heading text-2xl font-bold mb-2">This role is no longer open</h1>
          <p className="text-muted-foreground mb-6">
            It may have been filled or closed. Have a look at what else we're hiring for.
          </p>
          <button
            onClick={() => navigate('/careers')}
            className="inline-flex items-center gap-2 h-11 px-5 rounded-lg bg-primary text-primary-foreground font-semibold hover:opacity-90 transition"
          >
            <ArrowLeft className="w-4 h-4" /> View open roles
          </button>
        </div>
      </div>
    );
  }

  const meta = [
    job.location && { Icon: MapPin, label: job.location },
    { Icon: Clock, label: `${JOB_TYPES[job.job_type] || 'Full-time'} · ${WORK_MODES[job.work_mode] || 'On-site'}` },
    job.experience && { Icon: Briefcase, label: job.experience },
    job.salary_range && { Icon: IndianRupee, label: job.salary_range },
    job.openings > 1 && { Icon: Users, label: `${job.openings} openings` },
    job.apply_deadline && { Icon: CalendarDays, label: `Apply by ${fmtDate(job.apply_deadline)}` },
  ].filter(Boolean);

  return (
    <div className="min-h-screen bg-background">
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <section className="pt-24 pb-10 bg-gradient-to-br from-primary/10 via-background to-accent/5 border-b">
        <div className="max-w-4xl mx-auto px-4">
          <Link to="/careers" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition mb-5">
            <ArrowLeft className="w-4 h-4" /> All openings
          </Link>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <span className="inline-block px-3 py-1 bg-primary/10 text-primary text-xs font-semibold rounded-full mb-3">
              {job.department}
            </span>
            <h1 className="font-heading text-3xl md:text-4xl font-bold mb-4">{job.title}</h1>

            <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-muted-foreground mb-6">
              {meta.map(({ Icon, label }, i) => (
                <span key={i} className="flex items-center gap-1.5"><Icon className="w-4 h-4" />{label}</span>
              ))}
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => applyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                className="inline-flex items-center gap-2 h-11 px-6 rounded-lg bg-primary text-primary-foreground font-semibold hover:opacity-90 transition"
              >
                Apply for this role
              </button>
              <button
                onClick={share}
                className="inline-flex items-center gap-2 h-11 px-5 rounded-lg border font-semibold hover:border-primary hover:text-primary transition"
              >
                {copied ? <><Check className="w-4 h-4" /> Link copied</> : <><Share2 className="w-4 h-4" /> Share</>}
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Body ────────────────────────────────────────────────────────── */}
      <section className="py-14">
        <div className="max-w-4xl mx-auto px-4">
          {job.description && (
            <section className="mb-8">
              <h2 className="font-heading text-xl font-bold mb-3">About the role</h2>
              <p className="text-muted-foreground leading-relaxed whitespace-pre-line">{job.description}</p>
            </section>
          )}

          <BulletList title="What you'll do" items={job.responsibilities} />
          <BulletList title="What we're looking for" items={job.requirements} />
          <BulletList title="What we offer" items={job.benefits} />

          {/* ── Apply ─────────────────────────────────────────────────── */}
          <div ref={applyRef} className="scroll-mt-24">
            {submitted ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}
                className="text-center py-16 bg-card rounded-2xl border"
              >
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-7 h-7 text-green-600" />
                </div>
                <h3 className="font-heading text-xl font-bold mb-2">Application submitted</h3>
                <p className="text-muted-foreground mb-1">
                  You applied for <strong className="text-foreground">{job.title}</strong>
                </p>
                <p className="text-sm text-muted-foreground mb-6">
                  Our team reviews every application and will get back to you within 5 business days.
                </p>
                <Link
                  to="/careers"
                  className="inline-flex items-center gap-2 h-11 px-5 rounded-lg border font-semibold hover:border-primary hover:text-primary transition"
                >
                  View more roles
                </Link>
              </motion.div>
            ) : (
              <div className="bg-card rounded-2xl border p-5 sm:p-8">
                <h2 className="font-heading text-xl font-bold mb-1">Apply for this role</h2>
                <p className="text-sm text-muted-foreground mb-6">
                  Fields marked <span className="text-primary">*</span> are required. We'll only use these details for this application.
                </p>

                {errors.length > 0 && (
                  <div className="mb-5 rounded-lg border border-red-200 bg-red-50 p-3.5">
                    <p className="text-sm font-semibold text-red-800 flex items-center gap-1.5 mb-1">
                      <AlertCircle className="w-4 h-4" /> Please fix the following
                    </p>
                    <ul className="text-sm text-red-700 space-y-0.5 ml-5 list-disc">
                      {errors.map((er, i) => <li key={i}>{er}</li>)}
                    </ul>
                  </div>
                )}

                <form onSubmit={submit} className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Full name" required>
                      <input value={form.full_name} onChange={(e) => set('full_name', e.target.value)}
                        placeholder="Your name" className={inputCls} />
                    </Field>
                    <Field label="Email" required>
                      <input type="email" value={form.email} onChange={(e) => set('email', e.target.value)}
                        placeholder="you@example.com" className={inputCls} />
                    </Field>
                    <Field label="Phone" required>
                      <input value={form.phone} onChange={(e) => set('phone', e.target.value)}
                        placeholder="9876543210" className={inputCls} />
                    </Field>
                    <Field label="Current location">
                      <input value={form.current_location} onChange={(e) => set('current_location', e.target.value)}
                        placeholder="Bangalore" className={inputCls} />
                    </Field>
                    <Field label="Total experience">
                      <input value={form.experience_years} onChange={(e) => set('experience_years', e.target.value)}
                        placeholder="4 years" className={inputCls} />
                    </Field>
                    <Field label="Current company">
                      <input value={form.current_company} onChange={(e) => set('current_company', e.target.value)}
                        placeholder="Company name" className={inputCls} />
                    </Field>
                    <Field label="Notice period">
                      <input value={form.notice_period} onChange={(e) => set('notice_period', e.target.value)}
                        placeholder="30 days" className={inputCls} />
                    </Field>
                    <Field label="Expected CTC">
                      <input value={form.expected_ctc} onChange={(e) => set('expected_ctc', e.target.value)}
                        placeholder="₹12 LPA" className={inputCls} />
                    </Field>
                    <Field label="LinkedIn">
                      <input value={form.linkedin_url} onChange={(e) => set('linkedin_url', e.target.value)}
                        placeholder="https://linkedin.com/in/…" className={inputCls} />
                    </Field>
                    <Field label="Portfolio / GitHub">
                      <input value={form.portfolio_url} onChange={(e) => set('portfolio_url', e.target.value)}
                        placeholder="https://github.com/…" className={inputCls} />
                    </Field>
                  </div>

                  {/* Resume */}
                  <Field label="Resume" required>
                    <input ref={fileRef} type="file" accept=".pdf,.doc,.docx" onChange={pickResume} className="hidden" />
                    {resume ? (
                      <div className="flex items-center gap-3 rounded-lg border bg-background px-3.5 py-3">
                        <FileText className="w-5 h-5 text-primary flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium truncate">{resume.name}</p>
                          <p className="text-xs text-muted-foreground">{(resume.size / 1024 / 1024).toFixed(2)} MB</p>
                        </div>
                        <button type="button" onClick={() => setResume(null)}
                          className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button" onClick={() => fileRef.current?.click()}
                        className="w-full rounded-lg border border-dashed px-4 py-6 text-center hover:border-primary hover:bg-primary/[0.03] transition"
                      >
                        <Upload className="w-5 h-5 text-muted-foreground mx-auto mb-2" />
                        <span className="text-sm font-medium block">Upload your resume</span>
                        <span className="text-xs text-muted-foreground">PDF, DOC or DOCX · up to {MAX_RESUME_MB} MB</span>
                      </button>
                    )}
                  </Field>

                  <Field label="Why are you a good fit?">
                    <textarea value={form.cover_letter} onChange={(e) => set('cover_letter', e.target.value)}
                      rows={5} maxLength={5000}
                      placeholder="Tell us briefly about your relevant experience and why this role interests you."
                      className={areaCls} />
                  </Field>

                  {/* Honeypot — hidden from people, irresistible to bots */}
                  <input
                    type="text" name="website" tabIndex={-1} autoComplete="off"
                    value={form.website} onChange={(e) => set('website', e.target.value)}
                    className="hidden" aria-hidden="true"
                  />

                  <div className="flex flex-wrap items-center gap-3 pt-1">
                    <button
                      type="submit" disabled={submitting}
                      className="inline-flex items-center gap-2 h-11 px-6 rounded-lg bg-primary text-primary-foreground font-semibold hover:opacity-90 transition disabled:opacity-60"
                    >
                      {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : 'Submit application'}
                    </button>
                    {(job.apply_email || page?.contact_email) && (
                      <a
                        href={`mailto:${job.apply_email || page.contact_email}?subject=${encodeURIComponent('Application: ' + job.title)}`}
                        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition"
                      >
                        <Mail className="w-4 h-4" /> or email us directly
                      </a>
                    )}
                  </div>
                </form>
              </div>
            )}
          </div>

          {/* ── Related ───────────────────────────────────────────────── */}
          {related.length > 0 && (
            <section className="mt-14">
              <h2 className="font-heading text-xl font-bold mb-5">Other open roles</h2>
              <div className="space-y-4">
                {related.map((r, i) => <JobCard key={r.id} job={r} index={i} compact />)}
              </div>
            </section>
          )}
        </div>
      </section>
    </div>
  );
}
